import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Stack,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemDecorator
} from '@mui/joy';
import {
  FileText,
  Lightning as Zap,
  Package as BoxIcon,
  CurrencyDollar as DollarSign,
  Clock,
  CheckCircle,
  XCircle as AlertCircle
} from '@phosphor-icons/react';
import api from '../../lib/api';

interface EagleViewOrderModalProps {
  open: boolean;
  onClose: () => void;
  measurementId: number;
  address: string;
  onOrderPlaced: (orderData: any) => void;
}

interface PricingInfo {
  basic: number;
  premium: number;
  solar: number;
  expedite: number;
  model3d: number;
}

const PRICING: PricingInfo = {
  basic: 35,
  premium: 65,
  solar: 95,
  expedite: 25,
  model3d: 15
};

const TURNAROUND_TIMES = {
  standard: '1-4 hours',
  expedited: '30-60 minutes'
};

export const EagleViewOrderModal: React.FC<EagleViewOrderModalProps> = ({
  open,
  onClose,
  measurementId,
  address,
  onOrderPlaced
}) => {
  const [reportType, setReportType] = useState<'basic' | 'premium' | 'solar'>('premium');
  const [expedite, setExpedite] = useState(false);
  const [include3d, setInclude3d] = useState(false);
  const [isOrdering, setIsOrdering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateTotalPrice = () => {
    let total = PRICING[reportType];
    if (expedite) total += PRICING.expedite;
    if (include3d) total += PRICING.model3d;
    return total;
  };

  const handlePlaceOrder = async () => {
    setIsOrdering(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/measurements/${measurementId}/order_eagleview_report/`,
        {
          report_type: reportType,
          expedite,
          include_3d: include3d,
          additional_options: {
            address
          }
        }
      );

      onOrderPlaced(response.data);
      onClose();
    } catch (err: any) {
      console.error('Failed to place EagleView order:', err);
      if (err.response?.status === 409) {
        setError('An order is already in progress for this measurement.');
      } else {
        setError(err.response?.data?.error || 'Failed to place order. Please try again.');
      }
    } finally {
      setIsOrdering(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600 }}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FileText />
            <Typography level="h3">Order EagleView Report</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {/* Property Address */}
            <Box>
              <Typography level="body-sm" sx={{ mb: 0.5 }}>Property Address</Typography>
              <Typography level="body-lg" fontWeight="bold">{address}</Typography>
            </Box>

            {/* Report Type Selection */}
            <FormControl>
              <FormLabel>Report Type</FormLabel>
              <RadioGroup
                value={reportType}
                onChange={(e) => setReportType(e.target.value as 'basic' | 'premium' | 'solar')}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: reportType === 'basic' ? 'primary.500' : 'neutral.300',
                      borderRadius: 'md',
                      bgcolor: reportType === 'basic' ? 'primary.50' : 'transparent'
                    }}
                  >
                    <Radio value="basic" label={
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box>
                          <Typography fontWeight="bold">Basic Report</Typography>
                          <Typography level="body-sm">
                            Total area, basic measurements, and pitch
                          </Typography>
                        </Box>
                        <Chip color="neutral" variant="soft">${PRICING.basic}</Chip>
                      </Stack>
                    } />
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: reportType === 'premium' ? 'primary.500' : 'neutral.300',
                      borderRadius: 'md',
                      bgcolor: reportType === 'premium' ? 'primary.50' : 'transparent'
                    }}
                  >
                    <Radio value="premium" label={
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box>
                          <Typography fontWeight="bold">
                            Premium Report
                            <Chip size="sm" color="success" variant="soft" sx={{ ml: 1 }}>Recommended</Chip>
                          </Typography>
                          <Typography level="body-sm">
                            Detailed facets, waste factor, all measurements
                          </Typography>
                        </Box>
                        <Chip color="neutral" variant="soft">${PRICING.premium}</Chip>
                      </Stack>
                    } />
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: reportType === 'solar' ? 'primary.500' : 'neutral.300',
                      borderRadius: 'md',
                      bgcolor: reportType === 'solar' ? 'primary.50' : 'transparent'
                    }}
                  >
                    <Radio value="solar" label={
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box>
                          <Typography fontWeight="bold">Solar Report</Typography>
                          <Typography level="body-sm">
                            Includes shade analysis and TSRF values
                          </Typography>
                        </Box>
                        <Chip color="neutral" variant="soft">${PRICING.solar}</Chip>
                      </Stack>
                    } />
                  </Box>
                </Box>
              </RadioGroup>
            </FormControl>

            {/* Additional Options */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>Additional Options</Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: expedite ? 'warning.500' : 'neutral.300',
                    borderRadius: 'md',
                    bgcolor: expedite ? 'warning.50' : 'transparent'
                  }}
                >
                  <Checkbox
                    checked={expedite}
                    onChange={(e) => setExpedite(e.target.checked)}
                    label={
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Zap size={16} />
                            <Typography fontWeight="bold">Expedite Order</Typography>
                          </Stack>
                          <Typography level="body-sm">
                            {TURNAROUND_TIMES.expedited} turnaround (vs {TURNAROUND_TIMES.standard})
                          </Typography>
                        </Box>
                        <Chip color="warning" variant="soft">+${PRICING.expedite}</Chip>
                      </Stack>
                    }
                  />
                </Box>

                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: include3d ? 'primary.500' : 'neutral.300',
                    borderRadius: 'md',
                    bgcolor: include3d ? 'primary.50' : 'transparent'
                  }}
                >
                  <Checkbox
                    checked={include3d}
                    onChange={(e) => setInclude3d(e.target.checked)}
                    label={
                      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <BoxIcon size={16} />
                            <Typography fontWeight="bold">Include 3D Model</Typography>
                          </Stack>
                          <Typography level="body-sm">
                            Interactive 3D visualization of the property
                          </Typography>
                        </Box>
                        <Chip color="primary" variant="soft">+${PRICING.model3d}</Chip>
                      </Stack>
                    }
                  />
                </Box>
              </Stack>
            </Box>

            {/* What's Included */}
            <Box sx={{ bgcolor: 'neutral.50', p: 2, borderRadius: 'md' }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                {reportType === 'basic' ? 'Basic' : reportType === 'premium' ? 'Premium' : 'Solar'} Report Includes:
              </Typography>
              <List size="sm">
                <ListItem>
                  <ListItemDecorator>
                    <CheckCircle size={16} color="green" />
                  </ListItemDecorator>
                  Total roof area and squares
                </ListItem>
                <ListItem>
                  <ListItemDecorator>
                    <CheckCircle size={16} color="green" />
                  </ListItemDecorator>
                  Predominant pitch measurement
                </ListItem>
                {(reportType === 'premium' || reportType === 'solar') && (
                  <>
                    <ListItem>
                      <ListItemDecorator>
                        <CheckCircle size={16} color="green" />
                      </ListItemDecorator>
                      Individual facet measurements
                    </ListItem>
                    <ListItem>
                      <ListItemDecorator>
                        <CheckCircle size={16} color="green" />
                      </ListItemDecorator>
                      Ridge, valley, and eave lengths
                    </ListItem>
                    <ListItem>
                      <ListItemDecorator>
                        <CheckCircle size={16} color="green" />
                      </ListItemDecorator>
                      Recommended waste factor
                    </ListItem>
                  </>
                )}
                {reportType === 'solar' && (
                  <>
                    <ListItem>
                      <ListItemDecorator>
                        <CheckCircle size={16} color="green" />
                      </ListItemDecorator>
                      Shade analysis and TSRF values
                    </ListItem>
                    <ListItem>
                      <ListItemDecorator>
                        <CheckCircle size={16} color="green" />
                      </ListItemDecorator>
                      Solar access values
                    </ListItem>
                  </>
                )}
              </List>
            </Box>

            {/* Estimated Delivery */}
            <Alert
              startDecorator={<Clock />}
              color="neutral"
              variant="soft"
            >
              <Box>
                <Typography level="title-sm">Estimated Delivery</Typography>
                <Typography level="body-sm">
                  {expedite ? TURNAROUND_TIMES.expedited : TURNAROUND_TIMES.standard} from order placement
                </Typography>
              </Box>
            </Alert>

            {/* Error Message */}
            {error && (
              <Alert
                startDecorator={<AlertCircle />}
                color="danger"
                variant="soft"
              >
                {error}
              </Alert>
            )}

            {/* Price Summary */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                borderRadius: 'md',
                border: '2px solid',
                borderColor: 'primary.200'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography level="body-sm">Total Price</Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <DollarSign size={20} />
                    <Typography level="h2">{calculateTotalPrice()}</Typography>
                  </Stack>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                    Delivery: {expedite ? TURNAROUND_TIMES.expedited : TURNAROUND_TIMES.standard}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handlePlaceOrder}
            disabled={isOrdering}
            startDecorator={isOrdering ? <CircularProgress size="sm" /> : <FileText size={16} />}
          >
            {isOrdering ? 'Placing Order...' : `Place Order - $${calculateTotalPrice()}`}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};